import { useEffect, useState } from "react";

import {
    Button,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Textarea,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { HttpStatusCode } from "axios";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowIcon from "@/assets/ArrowIcon";
import { DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { applicationOptionSchema } from "@/schemas/applicationOptionSchema";
import { getApplicationOptionById, postApplicationOption, putApplicationOption } from "@/services/applicationOptionService";

const DATA_TYPES = [
    { value: "string", label: "String" },
    { value: "integer", label: "Integer" },
    { value: "boolean", label: "Boolean" },
    { value: "datetime", label: "DateTime" },
];

type ApplicationOptionFormValues = {
    name: string;
    value: string;
    dataType: string;
    description: string;
};

const getErrorMessage = (error: unknown): string | undefined =>
    typeof error === "string" ? error : undefined;

const EditApplicationOption = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        isOpen: isOpenConfirm,
        onOpen: onOpenConfirm,
        onClose: onCloseConfirm,
    } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);

    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'save' | 'cancel' | null>(null);
    const [pendingValues, setPendingValues] = useState<ApplicationOptionFormValues | null>(null);

    const { id } = location.state || {};
    const isEdit = Boolean(id);

    const fetchData = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const response = await getApplicationOptionById(id) as any;
            if (response.status === HttpStatusCode.Ok && response.data) {
                setData(response.data);
            } else {
                toast({
                    title: 'Load error',
                    description: response?.message || "Failed to load application option.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                navigate('/settings/application-options');
            }
        } catch (error) {
            toast({
                title: 'Load error',
                description: "An error occurred while loading the application option.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            navigate('/settings/application-options');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isEdit) {
            fetchData();
        }
    }, [id]);

    const handleSubmit = async (values: ApplicationOptionFormValues) => {
        setPendingValues(values);
        setConfirmAction('save');
        onOpenConfirm();
    };

    const handleConfirmSave = async (values: ApplicationOptionFormValues) => {
        try {
            const obj = {
                ...values,
                ...(isEdit && { id: data.id }),
                modifiedBy: name || "System",
                createdBy: name || "System",
            };

            const response = isEdit
                ? await putApplicationOption(obj) as any
                : await postApplicationOption(obj) as any;

            if (response.status === HttpStatusCode.Ok) {
                toast({
                    title: 'Success',
                    description: isEdit ? "Application option updated successfully." : "Application option created successfully.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
                setTimeout(() => navigate('/settings/application-options'), 700);
            } else {
                toast({
                    title: 'Save failed',
                    description: response?.message || "Failed to save application option.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Save error',
                description: "An error occurred while saving the application option.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    const handleCancel = () => {
        setConfirmAction('cancel');
        onOpenConfirm();
    };

    const handleConfirmCancel = () => {
        navigate('/settings/application-options');
    };

    const initialValues: ApplicationOptionFormValues = data || {
        name: '',
        value: '',
        dataType: 'string',
        description: '',
    };

    return (
        <div style={{ padding: '20px 15px', maxWidth: '600px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', cursor: 'pointer' }} onClick={() => navigate('/settings/application-options')}>
                <ArrowIcon style={{ transform: 'rotate(180deg)' }} />
                <span>Back</span>
            </div>

            <h2 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                {isEdit ? 'Edit Application Option' : 'Add Application Option'}
            </h2>

            {isLoading && !data && <div>Loading...</div>}

            {!isLoading && (
                <Formik<ApplicationOptionFormValues>
                    initialValues={initialValues}
                    validationSchema={applicationOptionSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ errors, touched, isSubmitting }) => (
                        <Form>
                            <div style={{ marginBottom: '15px' }}>
                                <Field name="name">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.name && touched.name)}>
                                            <FormLabel>Name *</FormLabel>
                                            <Input
                                                {...field}
                                                placeholder="e.g., DefaultEmailCC"
                                            />
                                            <FormErrorMessage>{getErrorMessage(errors.name)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <Field name="value">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.value && touched.value)}>
                                            <FormLabel>Value *</FormLabel>
                                            <Textarea
                                                {...field}
                                                placeholder="Enter the value"
                                                minHeight="100px"
                                            />
                                            <FormErrorMessage>{getErrorMessage(errors.value)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <Field name="dataType">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.dataType && touched.dataType)}>
                                            <FormLabel>Data Type *</FormLabel>
                                            <select
                                                {...field}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                }}
                                            >
                                                {DATA_TYPES.map(type => (
                                                    <option key={type.value} value={type.value}>{type.label}</option>
                                                ))}
                                            </select>
                                            <FormErrorMessage>{getErrorMessage(errors.dataType)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <Field name="description">
                                    {({ field }: FieldProps) => (
                                        <FormControl isInvalid={!!(errors.description && touched.description)}>
                                            <FormLabel>Description</FormLabel>
                                            <Textarea
                                                {...field}
                                                placeholder="Enter description (optional)"
                                            />
                                            <FormErrorMessage>{getErrorMessage(errors.description)}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    type="submit"
                                    colorScheme='blue'
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </Button>
                                <Button
                                    onClick={handleCancel}
                                    variant='outline'
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            )}

            <ModalConfirmation
                isOpen={isOpenConfirm}
                onClose={onCloseConfirm}
                title={confirmAction === 'save' ? 'Confirm Save' : 'Confirm Cancel'}
                confirmationText={confirmAction === 'save' 
                    ? 'Are you sure you want to save this application option?' 
                    : 'Are you sure you want to cancel? Unsaved changes will be lost.'}
                handleCancel={onCloseConfirm}
                handleConfirm={() => {
                    if (confirmAction === 'save' && pendingValues) {
                        handleConfirmSave(pendingValues);
                    } else {
                        handleConfirmCancel();
                    }
                    onCloseConfirm();
                }}
                confirmButtonText={confirmAction === 'save' ? 'Save' : 'Yes'}
                cancelButtonText={confirmAction === 'save' ? 'Cancel' : 'No'}
            />
        </div>
    );
};

export default EditApplicationOption;
